// Selectors for the original split comparison table.
const DEFAULT_SELECTORS = {
  table: '.l-table-list__table--comparison',
  labels: '.l-table-list__comparison-labels .l-table-list__row',
  values: '.l-table-list__comparison-scroll .l-table-list__row',
  spacer: '.l-table-list__comparison-spacer',
  head: '.l-table-list__head',
  headItems: '.l-table-list__head-item',
  comparison: '.l-table-list__comparison',
  scroll: '.l-table-list__comparison-scroll',
  indicator: '.l-table-list__indicator',
  indicatorThumb: '.l-table-list__indicator-thumb',
  valueText: '.l-table-list__value-text',
  header: '.l-header',
};

// Shared horizontal scroll indicator.
const updateScrollIndicator = ({ scroll, indicator, indicatorThumb }) => {
  if (!scroll || !indicator || !indicatorThumb) return;

  const maxScroll = Math.max(0, scroll.scrollWidth - scroll.clientWidth);
  const trackWidth = indicator.clientWidth;
  const progress = maxScroll > 0 ? scroll.scrollLeft / maxScroll : 0;
  const thumbWidth = maxScroll > 0
    ? Math.max(progress * trackWidth, 50)
    : trackWidth;

  indicator.hidden = maxScroll <= 0;
  indicatorThumb.style.width = `${thumbWidth}px`;
  indicatorThumb.style.left = '0';
};

const resetScrollIndicator = ({ indicator, indicatorThumb }) => {
  if (!indicator || !indicatorThumb) return;

  indicator.hidden = false;
  indicatorThumb.style.width = '';
  indicatorThumb.style.left = '';
};

// Original comparison table:
// - Header and body are split into separate DOM areas.
// - JS synchronizes row heights and header horizontal position.
class TableComparisonComponent {
  constructor(element, options = {}) {
    this.element = element;
    this.selectors = { ...DEFAULT_SELECTORS, ...options.selectors };
    this.desktopMediaQuery = window.matchMedia('(min-width: 768px)');
    this.refs = null;
    this.observer = null;
    this.onScroll = null;
    this.onWindowResize = null;
    this.syncFrame = null;
    this.isBound = false;
  }

  init() {
    if (this.isBound) return;

    this.collectRefs();
    this.bindEvents();
    this.sync();

    this.isBound = true;
  }

  collectRefs() {
    const labels = [...this.element.querySelectorAll(this.selectors.labels)];
    const values = [...this.element.querySelectorAll(this.selectors.values)];
    const headItems = [...this.element.querySelectorAll(this.selectors.headItems)];
    const columnCount = headItems.length;
    const rowCount = Math.min(labels.length, values.length);
    const invalidValueRow = values.find(
      (row) => row.querySelectorAll(this.selectors.valueText).length !== columnCount,
    );
    const isValid = labels.length === values.length && columnCount > 0 && !invalidValueRow;

    this.refs = {
      labels,
      values,
      head: this.element.querySelector(this.selectors.head),
      headItems,
      comparison: this.element.querySelector(this.selectors.comparison),
      scroll: this.element.querySelector(this.selectors.scroll),
      indicator: this.element.querySelector(this.selectors.indicator),
      indicatorThumb: this.element.querySelector(this.selectors.indicatorThumb),
      columnCount,
      rowCount,
      isValid,
    };

    this.element.style.setProperty('--comparison-columns', String(columnCount || 1));
    this.element.dataset.comparisonValid = String(isValid);

    if (!isValid) {
      console.warn('[TableListComparisonSync] Invalid comparison structure', {
        table: this.element,
        labelRows: labels.length,
        valueRows: values.length,
        columnCount,
      });
    }
  }

  bindEvents() {
    const { scroll, labels, values, head, comparison } = this.refs;

    if (scroll) {
      this.onScroll = () => {
        this.updateIndicator();
        this.updateStickyTranslate();
      };
      scroll.addEventListener('scroll', this.onScroll, { passive: true });
    }

    this.onWindowResize = () => this.scheduleSync();
    window.addEventListener('resize', this.onWindowResize);

    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.scheduleSync());

      labels.forEach((row) => this.observer.observe(row));
      values.forEach((row) => this.observer.observe(row));
      if (head) this.observer.observe(head);
      if (scroll) this.observer.observe(scroll);
      if (comparison) this.observer.observe(comparison);
    }
  }

  destroy() {
    if (!this.isBound || !this.refs) return;

    this.resetHeights();
    this.resetStickyTranslate();
    this.resetIndicator();

    if (this.refs.scroll && this.onScroll) {
      this.refs.scroll.removeEventListener('scroll', this.onScroll);
    }

    if (this.onWindowResize) {
      window.removeEventListener('resize', this.onWindowResize);
    }

    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.syncFrame) {
      window.cancelAnimationFrame(this.syncFrame);
    }

    this.onScroll = null;
    this.onWindowResize = null;
    this.observer = null;
    this.syncFrame = null;
    delete this.element.dataset.comparisonValid;
    this.refs = null;
    this.isBound = false;
  }

  scheduleSync() {
    if (this.syncFrame) return;

    this.syncFrame = window.requestAnimationFrame(() => {
      this.syncFrame = null;
      this.sync();
    });
  }

  updateStickyTranslate() {
    if (!this.refs || this.desktopMediaQuery.matches) return;

    const { scroll } = this.refs;
    if (!scroll) return;

    this.element.style.setProperty(
      '--comparison-sticky-translate-x',
      `-${scroll.scrollLeft}px`,
    );
  }

  resetStickyTranslate() {
    this.element.style.removeProperty('--comparison-sticky-top');
    this.element.style.removeProperty('--comparison-sticky-translate-x');
  }

  updateHeaderTop() {
    const header = document.querySelector(this.selectors.header);
    const headerHeight = header ? header.offsetHeight : 0;
    this.element.style.setProperty('--comparison-sticky-top', `${headerHeight}px`);
  }

  sync() {
    if (!this.refs) return;

    this.resetHeights();
    this.updateHeaderTop();
    this.updateIndicator();
    this.updateStickyTranslate();

    if (!this.refs.isValid) return;

    this.syncRowHeights();
  }

  resetHeights() {
    const { labels, values } = this.refs;

    labels.forEach((row) => {
      row.style.height = '';
    });

    values.forEach((row) => {
      row.style.height = '';
    });
  }

  syncRowHeights() {
    const { labels, values, rowCount } = this.refs;
    const rows = labels.slice(0, rowCount);

    const heights = rows.map((labelRow, index) => {
      const valueRow = values[index];
      if (!valueRow) return 0;
      return Math.max(labelRow.offsetHeight, valueRow.offsetHeight);
    });

    rows.forEach((labelRow, index) => {
      const valueRow = values[index];
      if (!valueRow) return;
      labelRow.style.height = `${heights[index]}px`;
      valueRow.style.height = `${heights[index]}px`;
    });
  }

  updateIndicator() {
    updateScrollIndicator(this.refs);
  }

  resetIndicator() {
    resetScrollIndicator(this.refs);
  }
}

// Public initializer that wires comparison tables on the page.
export class TableListComparisonSync {
  constructor(options = {}) {
    this.options = {
      selectors: { ...DEFAULT_SELECTORS, ...options.selectors },
    };
    this.components = [];
    this.onWindowLoad = null;

    this.init();
  }

  init() {
    this.destroyComponents();

    const tables = [...document.querySelectorAll(this.options.selectors.table)];
    this.components = tables.map((table) => new TableComparisonComponent(table, this.options));
    this.components.forEach((component) => component.init());

    if (!tables.length) return;

    if (!this.onWindowLoad) {
      this.onWindowLoad = () => this.refresh();
      window.addEventListener('load', this.onWindowLoad);
    }
  }

  refresh() {
    this.components.forEach((component) => component.sync());
  }

  destroyComponents() {
    this.components.forEach((component) => component.destroy());
    this.components = [];
  }

  destroy() {
    if (this.onWindowLoad) {
      window.removeEventListener('load', this.onWindowLoad);
      this.onWindowLoad = null;
    }

    this.destroyComponents();
  }
}
